import React, { useEffect, useRef, useState } from "react";
// Sprint 56b — every SlidePanel callsite is now a CenteredModal. Same
// chrome (frosted glass, blur backdrop), centered viewport.
import CenteredModal from "../../components/CenteredModal";
import Spinner from "../../components/Spinner";
import Confirm from "../../components/Confirm";
import Modal from "../../components/Modal";
import { TipoPill, EstadoPill, SlaPill, TipoFalloPill, ESTADO_LABEL, PREFER_SUB_TIPO_STATES } from "../../components/Pills";
import { formatTipo } from "../../utils/case_taxonomy";
import { cases, insumos as insumosApi, users as usersApi, moderation as moderationApi } from "../../api/endpoints";
import { useAuth } from "../../auth/AuthContext";
import { useToast } from "../../components/Toast";
import InsumosTab from "../insumos/InsumosTab";
import LitisoftFields from "../../components/LitisoftFields";
import LitisoftIncompleteModal from "../../components/LitisoftIncompleteModal";
import LitisoftMandatoryInline from "../../components/LitisoftMandatoryInline";
import { TERMINO_OPTIONS, formatTermino } from "../../utils/termino";
import {
  canMarkContestadaDirectly,
  canRequestModeration,
  availableContestadaCategorias,
  CONTESTADA_LABELS as CONTESTADA_LABELS_FULL,
} from "../../utils/permissions";
import LawyerActionPanel from "../../components/lawyer/LawyerActionPanel";
// feature/carpetas-proceso — Phase 1. Dedup banner at the top of every
// case detail view when the proceso already has a carpeta billed.
import ProcesoCarpetasBanner from "../../components/ProcesoCarpetasBanner";
// feature/carpetas-phase2-docs — P2. Per-case carpeta documents panel.
import CarpetaDocumentsPanel from "../../components/CarpetaDocumentsPanel";
// feature/carpeta-lawyer-visibility — P2.2 change 5. Compact status
// pill shown to viewers WITHOUT carpeta upload rights.
import CarpetaStatusBadge from "../../components/CarpetaStatusBadge";
// feature/single-role-audit — multi-role helper.
import { rolesOf } from "../../utils/permissions";
// feature/inline-accionante-edit — case-detail surface for the
// existing PATCH /api/personas/:id endpoint.
import InlineEditAccionante from "../../components/InlineEditAccionante";
// feature/tipo-fallo-edit — modal for changing tipo_fallo on fallos.
import TipoFalloEditModal from "../../components/TipoFalloEditModal";
import { PERSONA_EDIT_ROLES } from "../Personas";

import { fmt, InlineEdit, hasLawyerRole, MarcarContestada, CaseFlagsToggles, hasSancionesSpecialization, SancionLifecycle, MarcarInsumoDeficiente, SolicitarModeracion, LawyerAgencyButtons, TABS, CONTESTADA_LABELS } from "./shared";
import GeneralTab from "./GeneralTab";
import DecisionTab from "./DecisionTab";
import DocumentosTab from "./DocumentosTab";
import HistorialTab from "./HistorialTab";
import AuditoriaTab from "./AuditoriaTab";

export default function CaseDetail({ caseId, open, onClose, defaultTab = "general", onChanged }) {
  const { user } = useAuth();
  const toast = useToast();
  const [tab, setTab] = useState(defaultTab);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);

  // feature/stale-data-fix — when caseId changes (rapid navigation between
  // cases via the launcher / dispatcher), drop the previous case's data
  // immediately so the body can't render case A's content under case B's
  // header. The version ref invalidates any in-flight fetch whose result
  // would otherwise overwrite the new case.
  const fetchVersionRef = useRef(0);

  useEffect(() => {
    if (!open || !caseId) return;
    setTab(defaultTab);
    // 1. Wipe state synchronously so the panel falls back to the loading
    //    skeleton instead of mismatched data.
    setData(null);
    setLoading(true);
    // 2. Stamp this fetch's version; older in-flight responses will be
    //    discarded by the equality check below.
    const myVersion = ++fetchVersionRef.current;
    cases.get(caseId)
      .then(r => {
        if (myVersion !== fetchVersionRef.current) return; // newer fetch already started
        // 3. Final guard: refuse to commit data whose id doesn't match the
        //    URL/prop. Backend should never send mismatched ids, but the
        //    check is cheap and catches caching/proxy bugs early.
        if (r?.data && String(r.data.id) !== String(caseId)) {
          // eslint-disable-next-line no-console
          console.warn("CaseDetail id mismatch", { propCaseId: caseId, returnedId: r.data.id });
          return;
        }
        setData(r.data);
      })
      .catch((e) => {
        if (myVersion !== fetchVersionRef.current) return;
        // Sprint 60 — distinguish soft-deleted (case_deleted) from genuinely
        // missing (case_not_found). The backend returns these as `error`
        // codes in the 404 body; the human copy is in `message`.
        // Z2b — adds case_error_marcado as a third discriminator
        // (admin-set kill switch; restorable from /admin/maintenance).
        const code = e?.response?.data?.error;
        const msg  = e?.response?.data?.message;
        if (code === "case_deleted") {
          toast.push(msg || "Este caso fue eliminado y no puede modificarse.", "error");
          onClose?.();
        } else if (code === "case_error_marcado") {
          toast.push(msg || "Este caso fue marcado por error y está fuera del flujo activo.", "error");
          onClose?.();
        } else if (code === "case_not_found") {
          toast.push(msg || "Caso no encontrado", "error");
          onClose?.();
        } else {
          toast.push("No se pudo cargar el caso", "error");
        }
      })
      .finally(() => {
        if (myVersion !== fetchVersionRef.current) return;
        setLoading(false);
      });
  }, [open, caseId, defaultTab]);

  function refresh() {
    if (!caseId) return;
    const myVersion = ++fetchVersionRef.current;
    cases.get(caseId).then(r => {
      if (myVersion !== fetchVersionRef.current) return;
      if (r?.data && String(r.data.id) !== String(caseId)) return;
      setData(r.data);
    });
    onChanged?.();
  }

  if (!open) return null;
  // Render guard: if data exists but id doesn't match the prop, treat as
  // loading (defensive — should never happen with the version-ref pattern).
  const c = (data && String(data.id) === String(caseId)) ? data : null;
  const tabs = TABS.filter(t => {
    if (t.auditoriaOnly && c?.estado !== "en_auditoria") return false;
    if (t.decisionOnly) {
      // Visible only on fallo cases for revisor_fallos / admin
      if (c?.tipo !== "fallo") return false;
      // feature/single-role-audit — multi-role aware.
      if (!user || !["revisor_fallos","admin"].some(r => rolesOf(user).includes(r))) return false;
    }
    return true;
  });

  return (
    <CenteredModal
      open={open}
      onClose={onClose}
      title={c ? c.case_number : "Cargando…"}
      subtitle={c ? `${c.accionante_nombre || "—"} · ${c.juzgado || "—"}` : undefined}
      headerTags={c ? (
        <>
          <TipoPill tipo={c.tipo} />
          {c.tipo === "fallo" && <TipoFalloPill value={c.tipo_fallo} />}
          <EstadoPill c={c} />
          <SlaPill c={c} />
          {/* P2.2 change 5 — render the carpeta status pill for
              non-uploader viewers (lawyers). Uploaders see the full
              panel below; for them the pill would be redundant. */}
          {(() => {
            const r = (user?.roles && user.roles.length) ? user.roles : (user?.role ? [user.role] : []);
            const canUpload = ["admin", "carpetas", "directora"].some(x => r.includes(x));
            return canUpload ? null : <CarpetaStatusBadge caseId={c.id} />;
          })()}
        </>
      ) : null}
      size="xl"
      bodyless
    >
      {c && (
        <>
          <div className="slide-tabs">
            {tabs.map(t => (
              <div key={t.id} className={"slide-tab" + (tab === t.id ? " active" : "")} onClick={() => setTab(t.id)}>
                {t.label}
              </div>
            ))}
          </div>
          <div className="slide-body">
            {loading && <Spinner />}
            {/* feature/ui-polish-motion — key={tab} remounts the wrapper on
                tab switch so .tab-panel replays its 150ms fade+rise. */}
            <div key={tab} className="tab-panel">
              {tab === "general"    && <GeneralTab c={c} user={user} onChanged={refresh} onClose={onClose} />}
              {tab === "decision"   && <DecisionTab c={c} user={user} onChanged={refresh} onClose={onClose} />}
              {tab === "insumos"    && <InsumosTab c={c} user={user} onChanged={refresh} />}
              {tab === "documentos" && <DocumentosTab c={c} user={user} onChanged={refresh} />}
              {tab === "historial"  && <HistorialTab c={c} />}
              {tab === "auditoria"  && <AuditoriaTab c={c} user={user} onChanged={refresh} onClose={onClose} />}
            </div>
          </div>
        </>
      )}
      {!c && <div style={{ padding: 32, textAlign: "center" }}><Spinner size={20} /></div>}
    </CenteredModal>
  );
}

// ===========================================================================
// General tab
// ===========================================================================
